// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//
import xSetRmaReturnEmail from "@guestsupply/App/Handlers/RequestRma/SetRmaReturnEmail";
import xSetRmaReturnReplacement from "@guestsupply/App/Handlers/RequestRma/SetRmaReturnReplacement";
import xSetRmaReturnUPSLabel from "@guestsupply/App/Handlers/RequestRma/SetRmaReturnUPSLabel";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import validateEmail from "@insite/client-framework/Common/Utilities/validateEmail";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import Checkbox, { CheckboxProps } from "@insite/mobius/Checkbox";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden from "@insite/mobius/Hidden";
import Radio, { RadioComponentProps, RadioStyle } from "@insite/mobius/Radio";
import RadioGroup, { RadioGroupComponentProps } from "@insite/mobius/RadioGroup";
import TextField, { TextFieldProps } from "@insite/mobius/TextField";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import FieldSetPresentationProps, { FieldSetGroupPresentationProps } from "@insite/mobius/utilities/fieldSetProps";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks, useDispatch } from "react-redux";

const mapDispatchToProps = {
    xSetRmaReturnEmail,
    xSetRmaReturnReplacement,
    xSetRmaReturnUPSLabel,
};

export const enum rmaReceiveLabelConstants {
    customerInformation = "customerInformation",
    upsReturnLabel = "UPSReturnLabel",
    email = "RMAEmail",
    replacement = "RMAReplacement",
}

export interface RequestRmaReceiveLabelStyles {
    receiveLabelGridItem?: GridItemProps;
    receiveLabelsLabel?: TypographyProps;
    receiveLabelsRadio?: FieldSetPresentationProps<RadioComponentProps>;
    receiveLabelsRadioGroup?: FieldSetGroupPresentationProps<RadioGroupComponentProps>;

    fieldWrapper?: InjectableCss;
    requestReplacementCheckbox?: CheckboxProps;
    emailFieldWrapper?: InjectableCss;
}
export const requestRmaReceiveLabelStyles: RequestRmaReceiveLabelStyles = {};
const styles = requestRmaReceiveLabelStyles;

type Props = ResolveThunks<typeof mapDispatchToProps>;

const formId = "rmaRequestForm";

const RequestRmaReceiveLabel: React.FunctionComponent<Props> = ({
    xSetRmaReturnEmail,
    xSetRmaReturnReplacement,
    xSetRmaReturnUPSLabel,
}: Props) => {
    const [receiveLabelMethod, setReceiveLabelMethod] = useState("");
    const [email, setEmail] = useState("");
    const [hasReplacement, setHasReplacement] = useState(false);

    const handleReceiveLabelMethodChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setReceiveLabelMethod(e.currentTarget.value);
        xSetRmaReturnUPSLabel({ [rmaReceiveLabelConstants.upsReturnLabel]: e.currentTarget.value });
    };

    const emailChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
        const emailData = e.currentTarget.value.trim();
        setEmail(emailData);
        xSetRmaReturnEmail({ [rmaReceiveLabelConstants.email]: emailData });
    };

    const hasReplacementChangeHandler: CheckboxProps["onChange"] = (_, value) => {
        setHasReplacement(value);
        xSetRmaReturnReplacement({ [rmaReceiveLabelConstants.replacement]: value });
    };

    useEffect(() => {
        xSetRmaReturnEmail({ [rmaReceiveLabelConstants.email]: "" });
        xSetRmaReturnReplacement({ [rmaReceiveLabelConstants.replacement]: false });
        xSetRmaReturnUPSLabel({ [rmaReceiveLabelConstants.upsReturnLabel]: "" });
    }, []);

    // XNG-Changes
    return (
        <GridItem {...styles.receiveLabelGridItem}>
            <Typography {...styles.receiveLabelsLabel} as="p">
                {translate("Please indicate how you'd like to receive your UPS return labels:")}
            </Typography>
            <RadioGroup
                as="legend"
                value={receiveLabelMethod}
                onChangeHandler={handleReceiveLabelMethodChange}
                {...styles.receiveLabelsRadioGroup}
            >
                <Radio {...styles.receiveLabelsRadio} value="Email" data-test-selector="rma_email_return_label">
                    {translate("Email")}
                </Radio>
                <Hidden above="sm">
                    {receiveLabelMethod === "Email" && (
                        <StyledWrapper {...styles.emailFieldWrapper}>
                            <TextField
                                type="email"
                                name="email"
                                maxLength={125}
                                label={translate("Email")}
                                value={email}
                                onChange={emailChangeHandler}
                                required
                                {...{ formId }}
                            />
                        </StyledWrapper>
                    )}
                </Hidden>
                <Radio {...styles.receiveLabelsRadio} value="UPSDropOff" data-test-selector="rma_UPS_return_label">
                    {translate("UPS Drop Off")}
                </Radio>
            </RadioGroup>

            <Hidden below="md">
                {receiveLabelMethod === "Email" && (
                    <StyledWrapper {...styles.emailFieldWrapper}>
                        <TextField
                            type="email"
                            name="email"
                            maxLength={125}
                            label={translate("Email")}
                            value={email}
                            onChange={emailChangeHandler}
                            required
                            {...{ formId }}
                        />
                    </StyledWrapper>
                )}
            </Hidden>

            <StyledWrapper {...styles.fieldWrapper}>
                <Checkbox
                    {...styles.requestReplacementCheckbox}
                    checked={hasReplacement}
                    onChange={hasReplacementChangeHandler}
                >
                    {translate("Request Replacement  Order")}
                </Checkbox>
            </StyledWrapper>
        </GridItem>
    );
};

const widgetModule: WidgetModule = {
    component: connect(null, mapDispatchToProps)(RequestRmaReceiveLabel),
    definition: {
        group: "Return Request (RMA)",
        allowedContexts: ["RequestRmaPage"],
    },
};

export default widgetModule;
